import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

export const NAV_MENU = [
  {
    id: "yugioh",
    label: "Yu-Gi-Oh!",
    items: [
      { path: "/torneos", label: "Torneos", desc: "Eventos, flyers y resultados" },
      { path: "/formatos", label: "Formatos", desc: "GOAT, Edison, HAT, Speed Duel" },
      { path: "/banlist", label: "Banlist", desc: "Listas oficiales por formato" },
      { path: "/edison-cards", label: "Card Database", desc: "Busca cartas por formato" },
      { path: "/marketplace", label: "Marketplace", desc: "Cartas, sellado y accesorios", badge: "Tienda" },
    ],
  },
  {
    id: "comunidad",
    label: "Comunidad",
    items: [
      { path: "/comunidad", label: "Feed", desc: "Publicaciones de la comunidad" },
      { path: "/noticias", label: "Noticias", desc: "Eventos, banlists y decklists" },
      { path: "/tiendas", label: "Tiendas", desc: "Tiendas oficiales y aliadas" },
    ],
  },
  { id: "ranking", label: "Rankings", path: "/ranking" },
  {
    id: "juegos",
    label: "Juegos",
    items: [{ path: "/juegos/proto-tcg", label: "Proto TCG", desc: "Próximamente", badge: "Beta" }],
  },
];

const Dropdown = ({ menu, active }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-3 lg:px-4 py-2 font-semibold text-sm lg:text-base rounded-lg transition-all duration-300 ${
          active ? "bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-400 border border-pink-500/30" : "text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10"
        }`}
        data-testid={`nav-${menu.id}`}
        aria-expanded={open}
      >
        {menu.label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`absolute left-0 top-full pt-2 w-64 transition-all duration-200 ${open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none"}`}>
        <div className="glass-card rounded-xl p-2 shadow-2xl shadow-purple-900/40 border border-purple-500/30" data-testid={`nav-${menu.id}-menu`}>
          {menu.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-start justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-purple-800/40 transition-colors group"
              data-testid={`nav-item-${item.path.replace(/\//g, "-").slice(1)}`}
            >
              <div>
                <p className="text-sm font-semibold text-yellow-400 group-hover:text-pink-400 transition-colors">{item.label}</p>
                {item.desc && <p className="text-[11px] text-gray-400">{item.desc}</p>}
              </div>
              {item.badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">{item.badge}</span>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export const DesktopNav = () => {
  const { pathname } = useLocation();
  return (
    <nav className="hidden md:flex items-center gap-1 lg:gap-2" data-testid="navigation">
      {NAV_MENU.map((menu) =>
        menu.items ? (
          <Dropdown key={menu.id} menu={menu} active={menu.items.some((i) => pathname.startsWith(i.path))} />
        ) : (
          <Link
            key={menu.id}
            to={menu.path}
            className={`px-3 lg:px-4 py-2 font-semibold text-sm lg:text-base rounded-lg transition-all duration-300 ${
              pathname === menu.path ? "bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-400 border border-pink-500/30" : "text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10"
            }`}
            data-testid={`nav-${menu.id}`}
          >
            {menu.label}
          </Link>
        )
      )}
    </nav>
  );
};

export const MobileNav = ({ onNavigate }) => {
  const { pathname } = useLocation();
  const [openId, setOpenId] = useState(null);
  return (
    <nav className="flex flex-col gap-1 px-4" data-testid="mobile-navigation">
      <Link to="/" className="text-yellow-400 font-semibold py-3 px-4 rounded-lg hover:bg-purple-800/30" onClick={onNavigate}>Inicio</Link>
      {NAV_MENU.map((menu) =>
        menu.items ? (
          <div key={menu.id}>
            <button
              onClick={() => setOpenId(openId === menu.id ? null : menu.id)}
              className="w-full flex items-center justify-between text-yellow-400 font-semibold py-3 px-4 rounded-lg hover:bg-purple-800/30"
              data-testid={`mobile-nav-${menu.id}`}
            >
              {menu.label}
              <ChevronDown size={16} className={`transition-transform ${openId === menu.id ? "rotate-180" : ""}`} />
            </button>
            {openId === menu.id && (
              <div className="ml-4 border-l border-purple-700/50 pl-2 flex flex-col">
                {menu.items.map((item) => (
                  <Link key={item.path} to={item.path} onClick={onNavigate} className={`py-2.5 px-3 rounded-lg text-sm ${pathname === item.path ? "text-pink-400 bg-pink-500/10" : "text-gray-300 hover:bg-purple-800/30"}`}>
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Link key={menu.id} to={menu.path} onClick={onNavigate} className={`font-semibold py-3 px-4 rounded-lg ${pathname === menu.path ? "text-pink-400 bg-pink-500/10" : "text-yellow-400 hover:bg-purple-800/30"}`}>
            {menu.label}
          </Link>
        )
      )}
    </nav>
  );
};
